import { Box, Button, Flex } from '@chakra-ui/react'
import { useCallback, useRef } from 'react'

import { FormModal } from '../../../components/FormModal'
import { PaneHeader } from '../../../components/PaneHeader'
import { IArtField, TFrameValueMode } from '../../../services/Contests/interfaces'
import ArtFieldsEditor from './ArtFieldsEditor'
import ArtFieldsPreview from './ArtFieldsPreview'

/**
 * Returns a ref callback that makes an element scroll on mouse wheel.
 *
 * This modal opens nested inside the contest modal, so two `react-remove-scroll`
 * locks are active at once and the outer one `preventDefault()`s wheel events on
 * these custom scroll panes (dragging the scrollbar still works, the wheel does
 * not). Scrolling `scrollTop` manually is immune to that — `preventDefault` only
 * cancels the native scroll, not our own assignment.
 */
function useWheelScroll() {
  const cleanupRef = useRef<(() => void) | null>(null)

  return useCallback((el: HTMLDivElement | null) => {
    cleanupRef.current?.()
    cleanupRef.current = null
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (el.scrollHeight <= el.clientHeight) return // nothing to scroll
      e.preventDefault()
      // deltaMode: 0 = pixels, 1 = lines, 2 = pages — normalize to pixels.
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1
      el.scrollTop += e.deltaY * factor
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    cleanupRef.current = () => el.removeEventListener('wheel', onWheel)
  }, [])
}

interface IArtFieldsModalProps {
  isOpen: boolean
  onClose: () => void
  fields: IArtField[]
  notice: string | null
  onChange: (fields: IArtField[]) => void
  onNoticeChange: (notice: string | null) => void
  disabled?: boolean
  errors?: Record<string, string[]>
  lockedKeys?: string[]
  /** Mirrors the contest setting so the preview shows the frame value field. */
  frameValueMode?: TFrameValueMode
  /** Mirrors the contest setting so the preview shows the fixed starting bid notice. */
  defaultStartingBid?: number | null
}

/**
 * Large two-pane modal for editing the artwork data questions (ABRA-84).
 * Left pane = the existing editor (same rules/options); right pane = a faithful,
 * live preview of the public submission form. Each pane scrolls independently.
 * Edits flow straight back to the contest form (saved with the contest).
 */
export default function ArtFieldsModal({
  isOpen,
  onClose,
  fields,
  notice,
  onChange,
  onNoticeChange,
  disabled,
  errors,
  lockedKeys,
  frameValueMode,
  defaultStartingBid
}: IArtFieldsModalProps) {
  const editorPaneRef = useWheelScroll()
  const previewPaneRef = useWheelScroll()

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar campos de dados das obras"
      size="editor"
      contentProps={{ maxW: '95vw', w: '1500px', h: '92vh' }}
      footer={
        <>
          <Button colorScheme="magenta" onClick={onClose}>
            Concluir
          </Button>
        </>
      }
    >
      <Flex h="100%" direction={{ base: 'column', md: 'row' }}>
        {/* Editor pane */}
        <Box
          ref={editorPaneRef}
          flex="1"
          h="100%"
          overflowY="auto"
          borderRight={{ md: '1px solid' }}
          borderBottom={{ base: '1px solid', md: 'none' }}
          borderColor={{ base: 'gray.200', md: 'gray.200' }}
        >
          <PaneHeader icon="lni lni-pencil-alt" label="Edição dos campos" />
          <Box p={5}>
            <ArtFieldsEditor
              fields={fields}
              notice={notice}
              onChange={onChange}
              onNoticeChange={onNoticeChange}
              disabled={disabled}
              errors={errors}
              lockedKeys={lockedKeys}
            />
          </Box>
        </Box>

        {/* Preview pane */}
        <Box ref={previewPaneRef} flex="1" h="100%" overflowY="auto" bg="#0a0a0a">
          <PaneHeader icon="lni lni-eye" label="Pré-visualização do formulário" dark />
          <Box p={6}>
            <ArtFieldsPreview
              fields={fields}
              notice={notice}
              frameValueMode={frameValueMode}
              defaultStartingBid={defaultStartingBid}
            />
          </Box>
        </Box>
      </Flex>
    </FormModal>
  )
}
